import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaBriefcaseMedical, FaEnvelope, FaLock, FaUser, FaPhone,
  FaUserMd, FaUserInjured, FaEye, FaEyeSlash,
  FaHeartbeat, FaPills, FaStethoscope, FaClipboardList,
} from 'react-icons/fa';
import { useApp } from '../context/AppContext';

/* ─── tiny helpers ─────────────────────────────────────────── */
const PRIMARY   = '#E8500A';
const SECONDARY = '#FF6B2C';
const LIGHT     = '#FFF5F0';
const MUTED     = '#8888a8';
const DARK      = '#1a1a2e';

const inputBase = {
  width: '100%',
  padding: '12px 14px 12px 42px',
  border: '1.5px solid #e8e8f0',
  borderRadius: 10,
  fontSize: 14,
  color: DARK,
  background: '#fafafa',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s, background 0.2s',
};

const iconPin = {
  position: 'absolute',
  left: 14,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#b8b8d0',
  pointerEvents: 'none',
  fontSize: 14,
};

/* ─── floating info card on the right panel ─────────────────── */
const FloatCard = ({ icon: Icon, label, value, style }) => (
  <div style={{
    position: 'absolute',
    background: 'rgba(255,255,255,0.18)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderRadius: 14,
    padding: '9px 13px',
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    border: '1.5px solid rgba(255,255,255,0.32)',
    zIndex: 3,
    ...style,
  }}>
    <div style={{
      width: 34, height: 34, borderRadius: 9,
      background: 'rgba(255,255,255,0.22)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon color="white" size={15} />
    </div>
    <div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.72)', lineHeight: 1, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'white', lineHeight: 1.2 }}>{value}</div>
    </div>
  </div>
);

/* ─── main component ─────────────────────────────────────────── */
const Login = ({ role: roleProp = 'Patient', defaultMode = 'login' }) => {
  const { login, showToast } = useApp();
  const navigate = useNavigate();
  const mode = defaultMode;
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const isAdmin = roleProp === 'Admin';
  const [form, setForm]     = useState({
    name: '', email: '', password: '', confirmPassword: '',
    role: roleProp, phone: '', specialization: '', gender: '', bloodGroup: '',
  });

  const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'register' && form.password !== form.confirmPassword) {
      return showToast('Passwords do not match', 'error');
    }
    setLoading(true);
    try {
      const submitForm = { ...form, role: roleProp };
      if (!submitForm.gender) delete submitForm.gender;
      if (!submitForm.bloodGroup) delete submitForm.bloodGroup;

      const data = await login(submitForm, mode);
      if (mode === 'login') {
        const userRole = (data.user?.role || roleProp).toLowerCase();
        if (userRole !== roleProp.toLowerCase()) {
          showToast(`This login page is for ${roleProp}s only`, 'error');
          return;
        }
        navigate(`/${userRole}`, { replace: true });
      } else {
        setSignupSuccess(true);
        showToast(data.message, 'success');
      }
    } catch (err) {
      showToast(err.message || 'Authentication failed', 'error');
    }
    setLoading(false);
  };

  if (signupSuccess) {
    return (
      <div className="lp-wrap">
        <div className="lp-card" style={{ maxWidth: 500, padding: 40, background: 'white', textAlign: 'center', display: 'block' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#28a745', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaClipboardList color="white" size={30} />
            </div>
          </div>
          <h2 style={{ color: DARK, marginBottom: 15 }}>Signup Successful!</h2>
          <p style={{ color: MUTED, marginBottom: 30, lineHeight: 1.6 }}>
            Please check your email <b>{form.email}</b> to verify your account before logging in.
          </p>
          <button
            onClick={() => navigate(`/${roleProp.toLowerCase()}/login`)}
            style={{
              padding: '12px 30px', background: `linear-gradient(135deg,${PRIMARY},${SECONDARY})`,
              color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer'
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ── responsive styles ── */}
      <style>{`
        .lp-wrap   { min-height:100vh; background:#f0f2f5; display:flex; align-items:center; justify-content:center; padding:16px; font-family:'Segoe UI',sans-serif; }
        .lp-card   { width:100%; max-width:880px; border-radius:24px; overflow:hidden; box-shadow:0 24px 80px rgba(0,0,0,0.13); display:flex; min-height:570px; }
        .lp-left   { background:white; flex:0 0 52%; padding:48px 44px; display:flex; flex-direction:column; justify-content:center; overflow-y:auto; }
        .lp-right  { flex:0 0 48%; background:linear-gradient(145deg,#E8500A 0%,#FF6B2C 55%,#FF8C55 100%); position:relative; overflow:hidden; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:40px 28px; }
        .lp-input:focus { border-color:#E8500A !important; background:white !important; box-shadow:0 0 0 3px rgba(232,80,10,0.10) !important; }
        @media (max-width:720px){
          .lp-right  { display:none !important; }
          .lp-left   { flex:1 !important; padding:32px 22px !important; }
          .lp-card   { border-radius:18px !important; min-height:unset !important; }
        }
        @media (max-width:400px){
          .lp-left   { padding:24px 16px !important; }
        }
      `}</style>

      <div className="lp-wrap">
        <div className="lp-card">

          {/* ══════════════════════════════════════
              LEFT — form panel
          ══════════════════════════════════════ */}
          <div className="lp-left">

            {/* Logo row */}
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:30 }}>
              <div style={{
                width:40, height:40, borderRadius:10,
                background:`linear-gradient(135deg,${PRIMARY},${SECONDARY})`,
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 4px 14px rgba(232,80,10,0.32)',
              }}>
                <FaBriefcaseMedical size={18} color="white" />
              </div>
              <div>
                <div style={{ fontSize:14, fontWeight:800, color:DARK, lineHeight:1.2 }}>DocApps</div>
                <div style={{ fontSize:10, color:MUTED, lineHeight:1.2 }}>Solutions</div>
              </div>
            </div>

            {/* Title */}
            <h1 style={{ fontSize:26, fontWeight:800, color:DARK, margin:'0 0 5px' }}>
              {mode === 'login' ? 'Login To Your Account!' : 'Create Your Account!'}
            </h1>
            <p style={{ fontSize:13, color:MUTED, margin:'0 0 28px', lineHeight:1.5 }}>
              {mode === 'login'
                ? 'Welcome back — sign in to continue'
                : 'Fill in the details below to get started'}
            </p>

            {/* ── form ── */}
            <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:14 }}>

              {/* register-only fields */}
              {mode === 'register' && (
                <>
                  {/* Name */}
                  <div style={{ position:'relative' }}>
                    <span style={iconPin}><FaUser /></span>
                    <input
                      required className="lp-input"
                      value={form.name} onChange={set('name')}
                      placeholder="Full name"
                      style={inputBase}
                    />
                  </div>

                  {/* Role badge — role is fixed by the URL, just show it */}
                  <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', borderRadius:10, background:LIGHT, border:`1.5px solid ${PRIMARY}22` }}>
                    {roleProp === 'Doctor' ? <FaUserMd size={13} color={PRIMARY}/> : <FaUserInjured size={13} color={PRIMARY}/>}
                    <span style={{ fontSize:13, fontWeight:700, color:PRIMARY }}>Registering as {roleProp}</span>
                  </div>

                  {/* Phone */}
                  <div style={{ position:'relative' }}>
                    <span style={iconPin}><FaPhone /></span>
                    <input
                      className="lp-input"
                      value={form.phone} onChange={set('phone')}
                      placeholder="Phone number"
                      style={inputBase}
                    />
                  </div>

                  {/* Gender & Blood Group — patient only */}
                  {form.role === 'Patient' && (
                    <div style={{ display: 'flex', gap: 10 }}>
                      <select
                        className="lp-input"
                        value={form.gender} onChange={set('gender')}
                        style={{ ...inputBase, flex: 1, paddingLeft: 14 }}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                      <select
                        className="lp-input"
                        value={form.bloodGroup} onChange={set('bloodGroup')}
                        style={{ ...inputBase, flex: 1, paddingLeft: 14 }}
                      >
                        <option value="">Blood Group</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                      </select>
                    </div>
                  )}

                  {/* Specialization — doctor only */}
                  {form.role === 'Doctor' && (
                    <div style={{ position:'relative' }}>
                      <span style={iconPin}><FaStethoscope /></span>
                      <input
                        className="lp-input"
                        value={form.specialization} onChange={set('specialization')}
                        placeholder="Specialization  e.g. General Physician"
                        style={inputBase}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Email */}
              <div style={{ position:'relative' }}>
                <span style={iconPin}><FaEnvelope /></span>
                <input
                  required type="email" className="lp-input"
                  value={form.email} onChange={set('email')}
                  placeholder="Email address"
                  style={inputBase}
                />
              </div>

              {/* Password */}
              <div style={{ position:'relative' }}>
                <span style={iconPin}><FaLock /></span>
                <input
                  required className="lp-input"
                  type={showPwd ? 'text' : 'password'}
                  value={form.password} onChange={set('password')}
                  placeholder="Password"
                  style={{ ...inputBase, paddingRight:42 }}
                />
                <span
                  onClick={() => setShowPwd(p => !p)}
                  style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', color:'#b8b8d0', cursor:'pointer', fontSize:14 }}
                >
                  {showPwd ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>

              {/* Confirm password — register only */}
              {mode === 'register' && (
                <div style={{ position:'relative' }}>
                  <span style={iconPin}><FaLock /></span>
                  <input
                    required type="password" className="lp-input"
                    value={form.confirmPassword} onChange={set('confirmPassword')}
                    placeholder="Confirm password"
                    style={inputBase}
                  />
                </div>
              )}

              {/* Remember me + Forgot password (login only) */}
              {mode === 'login' && (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:-2 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, color:'#4a4a6a', cursor:'pointer', userSelect:'none' }}>
                    <input type="checkbox" style={{ accentColor:PRIMARY, width:15, height:15 }} />
                    Remember Me
                  </label>
                  <Link to="/forgot-password" style={{ fontSize:13, color:PRIMARY, fontWeight:600, cursor:'pointer', textDecoration: 'none' }}>
                    Forgot Password?
                  </Link>
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit" disabled={loading}
                style={{
                  width:'100%', padding:'14px 0', marginTop:4,
                  background: loading ? '#ddd' : `linear-gradient(135deg,${PRIMARY},${SECONDARY})`,
                  color:'white', border:'none', borderRadius:10,
                  fontSize:15, fontWeight:700, cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: loading ? 'none' : '0 4px 18px rgba(232,80,10,0.35)',
                  transition:'all 0.2s', letterSpacing:'0.3px',
                }}
              >
                {loading ? 'Please wait…' : (mode === 'login' ? 'Login' : 'Create Account')}
              </button>

              {/* Mode switcher link */}
              {!isAdmin && (
                <p style={{ textAlign:'center', fontSize:13, color:MUTED, margin:'2px 0 0' }}>
                  {mode === 'login' ? (
                    <>Not Registered Yet?&nbsp;
                      <span onClick={() => navigate(`/${roleProp.toLowerCase()}/signup`)} style={{ color:PRIMARY, fontWeight:700, cursor:'pointer' }}>Sign-Up</span>
                    </>
                  ) : (
                    <>Already have an account?&nbsp;
                      <span onClick={() => navigate(`/${roleProp.toLowerCase()}/login`)} style={{ color:PRIMARY, fontWeight:700, cursor:'pointer' }}>Sign In</span>
                    </>
                  )}
                </p>
              )}


            </form>
          </div>

          {/* ══════════════════════════════════════
              RIGHT — decorative panel
          ══════════════════════════════════════ */}
          <div className="lp-right">

            {/* dot-grid background */}
            <div style={{
              position:'absolute', inset:0,
              backgroundImage:'radial-gradient(rgba(255,255,255,0.18) 1.5px, transparent 1.5px)',
              backgroundSize:'22px 22px',
              zIndex:0,
            }} />

            {/* concentric bg circles */}
            {[340,240,150].map((s,i) => (
              <div key={i} style={{
                position:'absolute',
                width:s, height:s, borderRadius:'50%',
                background:`rgba(255,255,255,${0.05 + i*0.03})`,
                top:'50%', left:'50%',
                transform:'translate(-50%,-50%)',
                zIndex:1,
              }} />
            ))}

            {/* floating info cards */}
            <FloatCard
              icon={FaHeartbeat} label="Patients" value="Active Care"
              style={{ top:'12%', left:'6%' }}
            />
            <FloatCard
              icon={FaClipboardList} label="Records" value="Secured"
              style={{ top:'16%', right:'4%' }}
            />
            <FloatCard
              icon={FaPills} label="Pharmacy" value="Managed"
              style={{ bottom:'16%', right:'4%' }}
            />

            {/* center icon circle */}
            <div style={{
              position:'relative', zIndex:3,
              width:118, height:118, borderRadius:'50%',
              background:'rgba(255,255,255,0.2)',
              backdropFilter:'blur(8px)',
              WebkitBackdropFilter:'blur(8px)',
              border:'2.5px solid rgba(255,255,255,0.35)',
              display:'flex', alignItems:'center', justifyContent:'center',
              boxShadow:'0 8px 32px rgba(0,0,0,0.12)',
              marginBottom:22,
            }}>
              <FaStethoscope size={50} color="white" />
            </div>

            {/* branding text */}
            <div style={{ position:'relative', zIndex:3, textAlign:'center', color:'white' }}>
              <div style={{ fontSize:20, fontWeight:800, marginBottom:6, textShadow:'0 2px 8px rgba(0,0,0,0.15)' }}>
                DocApps Solutions
              </div>
              <div style={{ fontSize:13, opacity:.82, lineHeight:1.7 }}>
                Healthcare Management Platform<br/>
                <span style={{ opacity:.7, fontSize:12 }}>Doctors · Patients · Clinic</span>
              </div>
            </div>

            {/* decorative bottom-left leaf shape */}
            <div style={{
              position:'absolute', bottom:-40, left:-40, zIndex:1,
              width:140, height:140, borderRadius:'50%',
              background:'rgba(255,255,255,0.09)',
            }} />
            <div style={{
              position:'absolute', bottom:-20, left:-20, zIndex:1,
              width:90, height:90, borderRadius:'50%',
              background:'rgba(255,255,255,0.09)',
            }} />
          </div>

        </div>
      </div>
    </>
  );
};

export default Login;
